import {
  TextInput,
  PasswordInput,
  Paper,
  Title,
  Text,
  Container,
  Group,
  Button,
  Overlay,
  Divider,
} from '@mantine/core';
import {useState, useCallback} from "react";
import { auth, db } from "../api/api-firebase";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {Link, useNavigate} from "react-router-dom";
import {useForm} from "@mantine/form";
import { collection, getDocs, query, where, addDoc, serverTimestamp, updateDoc } from "firebase/firestore"
import { loginSuccess } from "../store/reducers/user"
import { updateMovieList } from "../store/reducers/movieList"
import { useAppDispatch } from "../utils/hooks"
import { showNotification } from "@mantine/notifications"
import { IconCheck, IconX, IconBrandGoogle } from "@tabler/icons"

export function UserSignin() {

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // const userCollectionRef = collection(db, "users");

  const [isLoading, setIsLoading] = useState<Boolean>(false)

  // Normalizza le email Gmail: rimuove i puntini e tutto dopo il + prima della @
  // Esempio: "valerio.ceniccola+test@gmail.com" -> "valerioceniccola@gmail.com"
  const normalizeGmail = useCallback((email: string | null | undefined): string | null => {
    if (!email) return null;

    const parts = email.split('@');
    if (parts.length !== 2) return email;

    const [localPart, domain] = parts;
    const domainLower = domain.toLowerCase();

    if (domainLower === 'gmail.com' || domainLower === 'googlemail.com') {
      // Rimuovo puntini e tutto dopo il + nella parte locale
      const normalizedLocal = localPart.replace(/\./g, '').split('+')[0];
      return `${normalizedLocal}@gmail.com`;
    }

    return email;
  }, []);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Email non valida'),
    },
  });

  const fetchUserData = async (userId: any) => {
    // Query per recuperare dettagli utente
    const qU = query(collection(db, "users"), where("uid", "==", userId));
    const doc = await getDocs(qU);

    // Query per recuperare la watchlist
    const qW = query(collection(db, "users", doc.docs[0].id, 'movieList'));
    const docWatchlist = await getDocs(qW);

    // Preparo l'array della watchlist
    let storeList: any[] = [];
    docWatchlist.docs.map((movie) => {
      let movieStored = movie.data();
      movieStored.id = movie.id;
      storeList = [...storeList, movieStored];
    })

    // Recupero docId dell'utente e i suoi dati del doc
    return {
      docId: doc.docs[0].id,
      userData: doc.docs[0].data(),
      watchList: storeList
    };
  }

  const handleLoginSuccess = (userData: any, docId: string, watchList: any[]) => {
    userData.docId = docId;

    dispatch(loginSuccess(userData));
    dispatch(updateMovieList(watchList));

    setIsLoading(false);

    showNotification({
      title: 'Hey! 👋🏻',
      message: "L'accesso è stato effettuato correttamente.",
      icon: <IconCheck />,
      color: 'green'
    })

    navigate("/");
  }

  const signInUser = async (values: any) => {

    setIsLoading(true);

    try {
      const data = await signInWithEmailAndPassword(
        auth,
        values.email.trim(),
        values.password
      );

      if (data) {
        const userId = data.user.uid;
        const email = data.user.email;

        // Cerco per uid
        let qU = query(collection(db, "users"), where("uid", "==", userId));
        let doc = await getDocs(qU);

        if (doc.docs.length > 0) {
          // Trovato per uid -> login normale
          const res = await fetchUserData(userId);
          handleLoginSuccess(res.userData, res.docId, res.watchList);
          return;
        }

        // Non trovato per uid: cerco per email normalizzata (risolve il caso in cui
        // l'uid è cambiato perché Google ha creato un nuovo account con email senza puntini)
        if (email) {
          const normalizedEmail = normalizeGmail(email);
          if (normalizedEmail) {
            qU = query(collection(db, "users"), where("email", "==", normalizedEmail));
            doc = await getDocs(qU);

            if (doc.docs.length > 0) {
              // Trovato per email: aggiorno l'uid nel documento esistente
              const existingDoc = doc.docs[0];
              await updateDoc(existingDoc.ref, { uid: userId, email: normalizedEmail });

              const res = await fetchUserData(userId);
              handleLoginSuccess(res.userData, res.docId, res.watchList);
              return;
            }
          }
        }

        // Non trovato né per uid né per email -> errore (non dovrebbe succedere)
        showNotification({
          title: 'Errore',
          message: "Account non trovato. Contatta il supporto.",
          icon: <IconX />,
          color: 'red'
        });
        setIsLoading(false);
      }
    } catch (err) {
      if (err instanceof Error) {
        err.message.includes('auth/user-not-found') ?
          showNotification({
            title: 'Utente inesistente',
            message: "L'utente che hai inserito non esiste",
            icon: <IconX />,
            color: 'red'
          })
          :
          showNotification({
            title: 'Errore generico',
            message: "Si è verificato un errore generico, riprova più tardi",
            icon: <IconCheck />,
            color: 'red'
          })

      }
      setIsLoading(false);
    }
  }

  const signInWithGoogle = async () => {
    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      if (result.user) {
        const email = result.user.email;
        const normalizedEmail = normalizeGmail(email);
        const userId = result.user.uid;

        // Cerco se l'utente esiste già in Firestore per uid
        let qU = query(collection(db, "users"), where("uid", "==", userId));
        let doc = await getDocs(qU);

        if (doc.docs.length > 0) {
          // Utente già registrato con questo uid -> login normale
          fetchUserData(userId).then(res => {
            handleLoginSuccess(res.userData, res.docId, res.watchList);
          });
          return;
        }

        // Se non trovato per uid, cerco per email normalizzata (risolve il problema dei puntini Gmail)
        if (normalizedEmail) {
          qU = query(collection(db, "users"), where("email", "==", normalizedEmail));
          doc = await getDocs(qU);

          if (doc.docs.length > 0) {
            // Utente già registrato con stessa email normalizzata -> aggiorno uid e faccio login
            const existingDoc = doc.docs[0];
            await updateDoc(existingDoc.ref, { uid: userId, email: normalizedEmail });

            fetchUserData(userId).then(res => {
              handleLoginSuccess(res.userData, res.docId, res.watchList);
            });
            return;
          }

          // Fallback per Gmail: se non trovato con email esatta, cerco tra tutti gli utenti
          // confrontando la versione normalizzata (risolve il caso in cui l'email salvata
          // in Firestore ha i puntini, es. "valerio.ceniccola@gmail.com" vs "valerioceniccola@gmail.com")
          if (normalizedEmail.endsWith('@gmail.com')) {
            const allUsers = await getDocs(collection(db, "users"));

            for (const userDoc of allUsers.docs) {
              const storedEmail = userDoc.data().email;
              if (storedEmail && normalizeGmail(storedEmail) === normalizedEmail) {
                await updateDoc(userDoc.ref, { uid: userId, email: normalizedEmail });

                fetchUserData(userId).then(res => {
                  handleLoginSuccess(res.userData, res.docId, res.watchList);
                });
                return;
              }
            }
          }
        }

        // Nuovo utente: creo il documento in Firestore
        const avatar = `https://robohash.org/${Math.floor(Math.random() * (10000 - 1) + 1)}.png`;
        const displayName = result.user.displayName || email?.split('@')[0] || 'Utente';

        const newUserRef = await addDoc(collection(db, "users"), {
          uid: userId,
          name: displayName,
          nameLowercase: displayName.toLowerCase(),
          email: normalizedEmail || email,
          signUpOn: serverTimestamp(),
          avatar
        });

        const newUserData = {
          uid: userId,
          name: displayName,
          nameLowercase: displayName.toLowerCase(),
          email: normalizedEmail || email,
          avatar
        };

        handleLoginSuccess(newUserData, newUserRef.id, []);
      }
    } catch (err: any) {
      showNotification({
        title: 'Errore',
        message: "Accesso con Google non riuscito. Riprova più tardi.",
        icon: <IconX />,
        color: 'red'
      });
      setIsLoading(false);
    }
  }

  return (
    <Container>

      <Title align="center" order={1}>Bentornato</Title>

      <Container size={420}>
        <Text color="dimmed" size="sm" align="center" mt={5}>
          Non sei ancora registrato?{' '}
          <Text component={Link} variant="link" to="/user/signup">Crea un account</Text>
        </Text>

        <Paper withBorder shadow="md" p={30} mt={30} radius="md" sx={{position: 'relative'}}>

          {isLoading && <Overlay opacity={0.3} color="#000"/>}

          <Button
            onClick={signInWithGoogle}
            fullWidth
            variant="default"
            color="gray"
            leftIcon={<IconBrandGoogle size={20} />}
            loading={isLoading && true}
          >
            Accedi con Google
          </Button>

          <Divider my="lg" label="oppure" labelPosition="center" />

          <form onSubmit={form.onSubmit((values) => signInUser(values))}>

            <TextInput label="Email" required {...form.getInputProps('email')} />
            <PasswordInput label="Password" required mt="md"  {...form.getInputProps('password')} />

            <Group position="apart" mt="md">
              {/*<Checkbox label="Remember me" />*/}
              <Text component={Link} variant="link" size="sm" to="/user/recover-password">Password dimenticata?</Text>
            </Group>

            <Button type="submit" fullWidth mt="xl" loading={isLoading && true}>Accedi</Button>

          </form>
        </Paper>

      </Container>
    </Container>
  );
}
